import { itemsAtom } from "@/atoms";
import type { StorefrontSectionKey } from "@/domain/storefront-appearance";
import { useBrowsingChannelCatalog } from "@/hooks/use-browsing-channel-catalog";
import { useBestsellers } from "@/hooks/use-bestsellers";
import type { Item, ItemOffer } from "@/types";
import { useAtomValue } from "jotai";
import { useMemo } from "react";

/** Max items the bestsellers rail shows. */
const BESTSELLERS_MAX = 10;

/** Below this the whole section hides: ranking too short for a rail. */
const BESTSELLERS_MIN_TO_SHOW = 3;

/** Sections whose rail is a row of menu items. */
export type ItemRailSectionKey = Extract<StorefrontSectionKey, "promotions" | "featured" | "bestsellers">;

/**
 * Stable empty reference for a section with nothing to show. A fresh `[]`
 * each render would be a new `useMemo` dependency every time.
 */
const NO_OFFERS: ItemOffer[] = [];

/** Rails only showcase what can be ordered: an out-of-stock offer or a paused item never enters one. */
function isOnSale(itemOffer: ItemOffer, pausedItemIds: ReadonlySet<string>): boolean {
  return itemOffer.status !== "UNAVAILABLE" && !pausedItemIds.has(itemOffer.itemId);
}

function pausedItemIdsOf(items: Item[]): Set<string> {
  return new Set(items.filter((item) => item.status === "UNAVAILABLE").map((item) => item.id));
}

/**
 * WHAT ENTERS EACH ITEM RAIL
 *
 * Seam between appearance and catalog. Appearance decides visibility, order,
 * and size; this hook decides WHICH items enter, the only place a new source
 * is wired. Catalog arrives already filtered by channel and availability window;
 * out-of-stock offers and paused items are dropped here.
 */
export function useSectionItemOffers(sectionKey: ItemRailSectionKey): ItemOffer[] {
  const { visibleItemOffers, visibleCategories, categoriesItemOffers } = useBrowsingChannelCatalog();
  const items = useAtomValue(itemsAtom);

  const bestsellerEntries = useBestsellers(sectionKey === "bestsellers");

  return useMemo(() => {
    const pausedItemIds = pausedItemIdsOf(items);
    const onSaleOffers = visibleItemOffers.filter((itemOffer) => isOnSale(itemOffer, pausedItemIds));

    switch (sectionKey) {
      case "promotions":
        return onSaleOffers.filter((itemOffer) => itemOffer.isPromotional);

      case "featured": {
        const seenItemIds = new Set<string>();
        const featured: ItemOffer[] = [];
        for (const category of visibleCategories) {
          for (const itemOffer of categoriesItemOffers[category.id] ?? []) {
            if (!itemOffer.isFeatured || seenItemIds.has(itemOffer.itemId)) continue;
            if (!isOnSale(itemOffer, pausedItemIds)) continue;
            seenItemIds.add(itemOffer.itemId);
            featured.push(itemOffer);
          }
        }
        return featured.length > 0 ? featured : NO_OFFERS;
      }

      case "bestsellers": {
        const offersByItemId = new Map(onSaleOffers.map((itemOffer) => [itemOffer.itemId, itemOffer]));

        const matched: ItemOffer[] = [];
        for (const entry of bestsellerEntries) {
          const itemOffer = offersByItemId.get(entry.merchantItemId);
          if (!itemOffer) continue;
          matched.push(itemOffer);
          if (matched.length === BESTSELLERS_MAX) break;
        }

        return matched.length >= BESTSELLERS_MIN_TO_SHOW ? matched : NO_OFFERS;
      }
    }
  }, [sectionKey, items, visibleItemOffers, visibleCategories, categoriesItemOffers, bestsellerEntries]);
}
